import {
  createTheme,
  type MantineColorsTuple,
  type MantineThemeOverride,
  Button,
  Input,
  TextInput,
  PasswordInput,
  Modal,
  Drawer,
  Badge,
  Table,
  NavLink,
  Select,
  MultiSelect,
  Tabs,
  Paper,
  ActionIcon,
  Menu,
} from '@mantine/core';

// ─── Operator Amber — the ONE accent color ────────────────────────────────────
const amber: MantineColorsTuple = [
  '#FFF8E1', // 0
  '#FFECB3', // 1
  '#FFE082', // 2
  '#FFD54F', // 3
  '#FFCA28', // 4
  '#FFC107', // 5
  '#FFB300', // 6
  '#D97706', // 7 ← primary
  '#B45309', // 8
  '#78350F', // 9
];

export const theme: MantineThemeOverride = createTheme({
  // ── Typography ────────────────────────────────────────────────────────────
  fontFamily: 'Geist, "SF Pro Display", system-ui, -apple-system, sans-serif',
  fontFamilyMonospace: '"Geist Mono", "JetBrains Mono", "SF Mono", "Fira Code", monospace',
  fontSizes: {
    xs: '11px',
    sm: '12px',
    md: '14px',
    lg: '16px',
    xl: '20px',
  },
  lineHeights: {
    xs: '1.4',
    sm: '1.4',
    md: '1.6',
    lg: '1.3',
    xl: '1.3',
  },

  // ── Colors ───────────────────────────────────────────────────────────────
  primaryColor: 'amber',
  primaryShade: 7,
  colors: { amber },

  // ── Radius — sharp corners, max 6px ──────────────────────────────────────
  defaultRadius: 'xs',
  radius: {
    xs: '4px',
    sm: '4px',
    md: '6px',
    lg: '6px',
    xl: '6px',
  },

  // ── Spacing ───────────────────────────────────────────────────────────────
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
  },

  // ── Focus ring ────────────────────────────────────────────────────────────
  focusRing: 'always',

  // ── Component overrides ───────────────────────────────────────────────────
  components: {
    Button: Button.extend({
      defaultProps: { radius: 'xs', size: 'sm' },
      styles: {
        root: {
          fontWeight: 500,
          letterSpacing: '0.01em',
          transition: 'background-color 120ms ease-out, transform 80ms ease-out, border-color 120ms ease-out',
          '&:active': {
            transform: 'scale(0.98) translateY(1px)',
          },
        },
      },
    }),

    ActionIcon: ActionIcon.extend({
      defaultProps: { radius: 'xs' },
    }),

    Paper: Paper.extend({
      styles: () => ({
        root: {
          backgroundColor: 'var(--surface)',
          borderColor: 'var(--boundary)',
          color: 'var(--text-primary)',
        },
      }),
    }),

    Input: Input.extend({
      defaultProps: { radius: 'xs', size: 'sm' },
      styles: () => ({
        input: {
          backgroundColor: 'var(--bg-input)',
          border: '1px solid var(--boundary)',
          borderRadius: '4px',
          color: 'var(--text-primary)',
          fontSize: '14px',
          '&::placeholder': { color: 'var(--text-disabled)' },
          '&:focus': {
            borderColor: 'var(--amber)',
            outline: 'none',
          },
        },
      }),
    }),

    TextInput: TextInput.extend({
      defaultProps: { size: 'sm', radius: 'xs' },
      styles: () => ({
        label: {
          fontSize: '11px',
          fontWeight: 500,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.06em',
          color: 'var(--text-secondary)',
          marginBottom: '6px',
        },
        input: {
          backgroundColor: 'var(--bg-input)',
          border: '1px solid var(--boundary)',
          color: 'var(--text-primary)',
          '&:focus': {
            borderColor: 'var(--amber)',
          },
        },
        error: {
          fontSize: '12px',
          marginTop: '4px',
        },
      }),
    }),

    PasswordInput: PasswordInput.extend({
      defaultProps: { size: 'sm', radius: 'xs' },
      styles: () => ({
        label: {
          fontSize: '11px',
          fontWeight: 500,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.06em',
          color: 'var(--text-secondary)',
          marginBottom: '6px',
        },
        input: {
          backgroundColor: 'var(--bg-input)',
          border: '1px solid var(--boundary)',
          color: 'var(--text-primary)',
        },
      }),
    }),

    Modal: Modal.extend({
      defaultProps: { radius: 'md', size: 'md' },
      styles: () => ({
        content: {
          backgroundColor: 'var(--surface)',
          border: '1px solid var(--boundary)',
          boxShadow: 'var(--modal-shadow)',
        },
        header: {
          backgroundColor: 'var(--surface)',
          borderBottom: '1px solid var(--boundary)',
          paddingBottom: '12px',
        },
        title: {
          color: 'var(--text-primary)',
          fontWeight: 600,
          fontSize: '16px',
        },
        close: {
          color: 'var(--text-secondary)',
        },
      }),
    }),

    Drawer: Drawer.extend({
      styles: () => ({
        content: {
          backgroundColor: 'var(--surface)',
          borderLeft: '1px solid var(--boundary)',
          boxShadow: 'var(--modal-shadow)',
        },
        header: {
          backgroundColor: 'var(--surface)',
          borderBottom: '1px solid var(--boundary)',
        },
        title: {
          color: 'var(--text-primary)',
          fontWeight: 600,
        },
      }),
    }),

    Badge: Badge.extend({
      defaultProps: { radius: 'xs', size: 'sm' },
      styles: {
        root: {
          textTransform: 'uppercase' as const,
          letterSpacing: '0.06em',
          fontFamily: 'inherit',
          fontWeight: 600,
        },
      },
    }),

    Table: Table.extend({
      defaultProps: { striped: false, highlightOnHover: true },
      styles: () => ({
        table: {
          borderCollapse: 'collapse',
          width: '100%',
        },
        th: {
          fontSize: '11px',
          fontWeight: 500,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.08em',
          color: 'var(--text-secondary)',
          backgroundColor: 'var(--bg-root)',
          borderBottom: '2px solid var(--boundary)',
          padding: '0 16px',
          height: '36px',
          whiteSpace: 'nowrap' as const,
        },
        td: {
          fontSize: '14px',
          color: 'var(--text-primary)',
          borderBottom: '1px solid var(--boundary-subtle)',
          padding: '0 16px',
          height: '44px',
        },
        tr: {
          cursor: 'pointer',
        },
      }),
    }),

    NavLink: NavLink.extend({
      styles: () => ({
        root: {
          borderRadius: '4px',
          height: '36px',
          padding: '0 12px',
          fontSize: '14px',
          color: 'var(--text-secondary)',
        },
        label: { fontWeight: 400 },
      }),
    }),

    Select: Select.extend({
      defaultProps: { size: 'sm', radius: 'xs' },
      styles: () => ({
        label: {
          fontSize: '11px',
          fontWeight: 500,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.06em',
          color: 'var(--text-secondary)',
          marginBottom: '6px',
        },
        input: {
          backgroundColor: 'var(--bg-input)',
          border: '1px solid var(--boundary)',
          color: 'var(--text-primary)',
        },
      }),
    }),

    MultiSelect: MultiSelect.extend({
      defaultProps: { size: 'sm', radius: 'xs' },
      styles: () => ({
        label: {
          fontSize: '11px',
          fontWeight: 500,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.06em',
          color: 'var(--text-secondary)',
          marginBottom: '6px',
        },
        input: {
          backgroundColor: 'var(--bg-input)',
          border: '1px solid var(--boundary)',
          color: 'var(--text-primary)',
        },
      }),
    }),

    Tabs: Tabs.extend({
      styles: () => ({
        tab: {
          fontSize: '14px',
          color: 'var(--text-secondary)',
          borderBottom: '2px solid transparent',
          padding: '8px 16px',
          fontWeight: 400,
        },
        list: {
          borderBottom: '1px solid var(--boundary)',
          gap: 0,
        },
      }),
    }),

    Menu: Menu.extend({
      styles: () => ({
        dropdown: {
          backgroundColor: 'var(--surface)',
          border: '1px solid var(--boundary)',
          boxShadow: 'var(--popover-shadow)',
        },
        item: {
          color: 'var(--text-primary)',
        },
      }),
    }),
  },
});
